import { Component, InfernoNode } from "inferno";
import { Router } from "inferno-router";
import {
  createBrowserHistory,
  History,
  Location,
  To,
  parsePath,
} from "history";
import { getBasePath } from "@utils/env";

type Props = {
  children?: InfernoNode;
};

/**
 * Browser router with basename support (inferno-router's BrowserRouter
 * does not forward basename to history v5).
 *
 * Important: do not Object.assign/spread the history object when adding a
 * `location` getter — assign invokes getters and freezes a stale location,
 * which breaks client-side navigation.
 */
export class BasePathBrowserRouter extends Component<Props, never> {
  history: History;

  constructor(props: Props, context: object) {
    super(props, context);
    const basename = getBasePath();
    const raw = createBrowserHistory();

    if (!basename) {
      this.history = raw;
      return;
    }

    const strip = (pathname: string) => {
      if (pathname === basename) {
        return "/";
      }
      if (pathname.startsWith(`${basename}/`)) {
        return pathname.slice(basename.length) || "/";
      }
      return pathname;
    };

    const prefixPathname = (pathname: string) =>
      pathname === "/" ? basename : `${basename}${pathname}`;

    const prefix = (to: To): To => {
      if (typeof to === "string") {
        const parsed = parsePath(to);
        return {
          ...parsed,
          pathname: prefixPathname(parsed.pathname || "/"),
        };
      }
      return {
        ...to,
        pathname: prefixPathname(to.pathname || "/"),
      };
    };

    const wrapLocation = (loc: Location): Location => ({
      ...loc,
      pathname: strip(loc.pathname),
    });

    this.history = {
      get action() {
        return raw.action;
      },
      get location() {
        return wrapLocation(raw.location);
      },
      createHref(to) {
        return raw.createHref(prefix(to));
      },
      push(to, state) {
        raw.push(prefix(to), state);
      },
      replace(to, state) {
        raw.replace(prefix(to), state);
      },
      go(delta) {
        raw.go(delta);
      },
      back() {
        raw.back();
      },
      forward() {
        raw.forward();
      },
      listen(listener) {
        return raw.listen(({ action, location }) => {
          listener({ action, location: wrapLocation(location) });
        });
      },
      block(blocker) {
        return raw.block(blocker);
      },
    };
  }

  render() {
    return (
      // @ts-expect-error Router children typing differs from InfernoNode
      <Router history={this.history}>{this.props.children}</Router>
    );
  }
}
